import { useHover, useMergedRef } from "@mantine/hooks";
import { faEllipsis } from "@fortawesome/pro-solid-svg-icons";
import cardStyles from "../../card.module.scss";
import styles from "./card-metric.module.scss";
import classNames from "classnames";
import type { CustomCardMetricFactory, ICardMetricSkeletonProps, IProps } from "./card-metric.types";
import { polymorphicComponentWrapper } from "../../../../utilities";
import { Card } from "../../card-component";
import { Group } from "../../../group";
import { Stack } from "../../../stack";
import { Text } from "../../../text";
import { Center } from "../../../center";
import { Icon } from "../../../icon";
import { Title } from "../../../title";
import { Box } from "../../../box";
import { isValidElement } from "react";
import { AutoMenu } from "../../../auto-menu";
import { ActionIcon } from "../../../action-icon";

const CardMetric = polymorphicComponentWrapper<CustomCardMetricFactory, IProps>(
    "CardMetric",
    ({ title, subtitle, icon, metric, color, menu, ...props }, ref) => {
        const { hovered, ref: hoverRef } = useHover();
        const mergedRef = useMergedRef(ref, hoverRef);

        return (
            <Card
                ref={mergedRef}
                data-color={color}
                classNames={{
                    root: classNames(cardStyles["root"], styles["root"]),
                    section: classNames(cardStyles["section"], styles["section"]),
                }}
                {...props}
                bdrs="lg"
                shadow="none"
            >
                <Stack gap="xl">
                    <Group id="header">
                        <Stack gap="0" flex={1}>
                            <Text variant="medium" size="sm">
                                {title}
                            </Text>
                            <Text size="xxs" dimmed>
                                {subtitle}
                            </Text>
                        </Stack>
                        <Box className={styles["actions"]}>
                            {typeof icon !== "undefined" && (
                                <Center
                                    className={styles["iconWrapper"]}
                                    data-visibility={
                                        !hovered || (hovered && typeof menu === "undefined") ? "visible" : "none"
                                    }
                                >
                                    {isValidElement(icon) ? icon : <Icon size="sm" icon={icon as any} />}
                                </Center>
                            )}
                            {typeof menu !== "undefined" && (
                                <AutoMenu
                                    floatingStrategy="fixed"
                                    withinPortal={false}
                                    items={menu}
                                    keepMounted
                                    className={styles["actionMenu"]}
                                    data-visibility={hovered ? "visible" : "none"}
                                >
                                    <ActionIcon
                                        bdrs="xl"
                                        size="input-xs"
                                        icon={faEllipsis}
                                        variant="default"
                                        color="gray"
                                    />
                                </AutoMenu>
                            )}
                        </Box>
                    </Group>
                    <Group id="metrics" gap="xl">
                        {metric.map(({ title, value, suffix, footer }, index) => (
                            <Stack key={`metric-${index}`} gap="sm">
                                <Stack gap="0">
                                    {typeof title !== "undefined" && (
                                        <Text variant="medium" size="xxs">
                                            {title}
                                        </Text>
                                    )}
                                    <Group gap="sm">
                                        <Title order={2}>{value}</Title>
                                        {typeof suffix !== "undefined" && (
                                            <Box className={styles["suffix"]}>
                                                <Text variant="bold" size="xs">
                                                    {suffix}
                                                </Text>
                                            </Box>
                                        )}
                                    </Group>
                                </Stack>
                                {typeof footer !== "undefined" && <Text size="sm">{footer}</Text>}
                            </Stack>
                        ))}
                    </Group>
                </Stack>
            </Card>
        );
    }
);

const CardMetricSkeleton = (props: ICardMetricSkeletonProps) => {
    const skeletonColor = "var(--surface-gray-transparent-color)";
    return (
        <Card
            data-color="gray"
            classNames={{
                root: classNames(cardStyles["root"], styles["root"]),
                section: classNames(cardStyles["section"], styles["section"]),
            }}
            role="status"
            aria-busy="true"
            {...props}
        >
            <Stack gap="xl">
                <Group>
                    <Stack gap="0" flex={1}>
                        <Text.Skeleton color={skeletonColor} disableDarken size="sm" width={120} />
                    </Stack>
                    <ActionIcon.Skeleton color={skeletonColor} disableDarken bdrs="xl" size="input-xs" />
                </Group>
                <Group id="metrics" gap="xl">
                    <Stack gap="sm">
                        <Group gap="sm">
                            <Title.Skeleton color={skeletonColor} disableDarken order={2} width={40} />
                            <Text.Skeleton color={skeletonColor} disableDarken size="xs" width={50} />
                        </Group>
                        <Text.Skeleton color={skeletonColor} disableDarken size="sm" width={80} />
                    </Stack>
                </Group>
            </Stack>
        </Card>
    );
};

CardMetric.Skeleton = CardMetricSkeleton;

export { CardMetric, CardMetricSkeleton };
